import { useApiRequestHelper } from '@/composables/useApiRequestHelper'
import useDayJs from '@/composables/useDayJs.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

export const useStartWorkCheckStore = defineStore(
  'startWorkCheck',
  () => {
    const { $apiAuthenticatedRequest } = useApiRequestHelper()
    const { t } = useI18n()

    const hasStartedChecks = ref(false)
    const hazards = ref([])
    const check = ref(null)
    const checkTasks = ref([])
    const checkHazards = ref([])

    const fetchTasks = async (workOrderId) => {
      return await $apiAuthenticatedRequest
        .index(
          'start-work-check-tasks',
          $apiAuthenticatedRequest.configs.index
            .setFields({
              'start-work-check-tasks': ['hazard_id', 'task_type', 'task_order', 'task', 'link'],
            })
            .setSort('task_order'),
        )
        .then(({ data }) => {
          checkTasks.value = [
            {
              title: useTranslationPath('stores.authenticated.startWorkCheckStore', 'induction'),
              tasks: [],
            },
            {
              title: useTranslationPath('stores.authenticated.startWorkCheckStore', 'hazards'),
              tasks: [],
              hazards: [],
            },
            {
              title: useTranslationPath(
                'stores.authenticated.startWorkCheckStore',
                'safeToProceed',
              ),
              tasks: [],
            },
          ]

          data
            .filter((check) => check.task_type === 'HAZARD')
            .forEach((check) => {
              if (checkHazards.value.findIndex((hazard) => hazard.id === check.id) === -1) {
                checkHazards.value.push(check)
              }
            })

          data
            .filter((check) => check.task_type !== 'HAZARD')
            .forEach((check) => {
              checkTasks.value.at(check.task_type === 'INDUCTION' ? 0 : 2).tasks.push({
                ...check,
                value: null,
                disabled: false,
              })
            })
        })
    }

    const fetchStartWorkChecks = async () => {
      return await $apiAuthenticatedRequest.index(
        'work-order-start-work-checks',
        $apiAuthenticatedRequest.configs.index.setIncludes(['workOrderStartWorkCheckTasks']),
      )
    }

    const createStartWorkCheck = async (data, includes = ['workOrderStartWorkCheckTasks']) => {
      return await $apiAuthenticatedRequest.post(
        'work-order-start-work-checks',
        $apiAuthenticatedRequest.configs.post.setData(data).setIncludes(includes),
      )
    }

    const updateStartWorkCheck = async (startWorkCheckId) => {
      return await $apiAuthenticatedRequest.patch(
        'work-order-start-work-checks',
        $apiAuthenticatedRequest.configs.patch
          .setId(startWorkCheckId)
          .setData({ completed_at: useDayJs().toISOString() }),
      )
    }

    const updateStartWorkCheckTask = async (startWorkCheckTaskId, answer) => {
      return await $apiAuthenticatedRequest.patch(
        'work-order-start-work-check-tasks',
        $apiAuthenticatedRequest.configs.patch.setId(startWorkCheckTaskId).setData({ answer }),
      )
    }

    const fetchHazards = async () => {
      return await $apiAuthenticatedRequest
        .index('hazards', $apiAuthenticatedRequest.configs.index.setSort('name'))
        .then(({ data }) => {
          hazards.value = data
        })
    }

    return {
      hasStartedChecks,
      hazards,
      check,
      checkTasks,
      checkHazards,
      fetchTasks,
      fetchStartWorkChecks,
      createStartWorkCheck,
      updateStartWorkCheck,
      updateStartWorkCheckTask,
      fetchHazards,
    }
  },
  { persist: true },
)
